import {AnizoneSource} from './AnizoneSource.ts';
import { loadKeiyoushiRepository } from '../repositories/KeiyoushiRepoParser.ts';
import { yomiriDatabase } from '../storage/YomiriDatabase.ts';
import { AnimeFlvSource } from './AnimeFlvSource.ts';
import { AnimeFlvOrSource } from './AnimeFlvOrSource.ts';
import { HtmlVideoSource } from './HtmlVideoSource.ts';
import { JkAnimeSource } from './JkAnimeSource.ts';
import { AnimeAv1Source } from './AnimeAv1Source.ts';
import type { AnimeSource } from './types.ts';
import type { YomiriHttpClient } from '../network/types.ts';
import { SessionResourceCache } from '../core/SessionResourceCache.ts';
import { getSourceCacheScope } from '../core/SourceSession.ts';
import { getRemoteSnapshot, remoteCall } from '../cast/RemoteSession.ts';
import { resolveAnimeServers } from './ResolveAnimeServers.ts';

const metadataCache = new SessionResourceCache();
const repositoryCache = new SessionResourceCache(5, 30 * 60_000);
if (typeof window !== 'undefined') window.addEventListener('yomiri-transport-changed', () => metadataCache.clear());

export const YUZONO_REPOSITORY='https://raw.githubusercontent.com/yuzono/anime-repo/repo/index.min.json';
export interface AnimeRepositoryEntry { key:string;name:string;language:string;iconUrl:string;website:string;connectorId?:string }
const connectors: Record<string, string> = {
  animeav1: 'animeav1',
  animeflv: 'animeflv',
  jkanime: 'jkanime',
};
export async function loadAnimeRepository(signal?: AbortSignal, refresh = false): Promise<AnimeRepositoryEntry[]> {
  if (!signal) return repositoryCache.get(YUZONO_REPOSITORY, () => readAnimeRepository(), refresh);
  return readAnimeRepository(signal);
}
async function readAnimeRepository(signal?: AbortSignal): Promise<AnimeRepositoryEntry[]> {
  const repo = await loadKeiyoushiRepository(YUZONO_REPOSITORY, { signal });
  return repo.extensions.flatMap(extension => extension.sources.filter(s => s.lang === 'es' && !extension.nsfw).map(s => ({
    key: `${extension.pkg}:${s.id}`,
    name: s.name,
    language: s.lang,
    iconUrl: extension.iconUrl,
    website: s.baseUrl,
    connectorId: connectors[extension.pkg.split('.').pop() || ''] || connectors[s.id] || (s.id.toLowerCase().includes('flv') ? 'animeflv' : s.id.toLowerCase().includes('jkanime') ? 'jkanime' : undefined),
  }))).sort((a, b) => Number(!!b.connectorId) - Number(!!a.connectorId) || a.name.localeCompare(b.name));
}
export async function installedAnimeSources(): Promise<string[]> {
  const remote=getRemoteSnapshot();if(remote)return remote.anime;
  try {
    const repositories: string[] = JSON.parse(localStorage.getItem('yomiri_repositories') || '[]');
    if (repositories.length === 0) return [];
  } catch { return []; }
  const value = await yomiriDatabase.getSetting<unknown>('anime_installed_sources', []);
  const migrated = Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
  return migrated;
}
export async function setInstalledAnimeSources(ids: string[]): Promise<void> {
  await yomiriDatabase.setSetting('anime_installed_sources', [...new Set(ids)]);
  window.dispatchEvent(new Event('yomiri-sources-changed'));
}
export function createAnimeSource(id:string,http:YomiriHttpClient):AnimeSource {
  const remote=getRemoteSnapshot();
  if(remote){
    if(!remote.anime.includes(id))throw new Error('Esta fuente no está instalada en el móvil.');
    const call=<T>(operation:string,args:unknown[])=>remoteCall<T>('anime.'+operation,{sourceId:id,args});
    return {id,name:id,website:'',lang:'es',
      getPopular:(page=1)=>call('getPopular',[page]),getLatest:(page=1)=>call('getLatest',[page]),
      search:(query,page=1,filters={})=>call('search',[query,page,filters]),
      getAnime:anime=>call('getAnime',[anime]),getEpisodes:anime=>call('getEpisodes',[anime]),
      getServers:episode=>call('getServers',[episode]),getSearchFilters:()=>call('getSearchFilters',[])};
  }
const source: AnimeSource | undefined = id === 'anizone' ? new AnizoneSource(http) : id === 'katanime' || id === 'animeyt' || id === 'monoschinos' || id === 'lacartoons' ? new HtmlVideoSource(id,http) : id === 'animeflvor' ? new AnimeFlvOrSource(http) : id === 'animeav1' ? new AnimeAv1Source(http) : id === 'animeflv' ? new AnimeFlvSource(http) : id === 'jkanime' ? new JkAnimeSource(http) : undefined;
  if (!source) throw new Error('Esta extensión todavía necesita un conector web.');
  const cache = <T>(operation: string, args: unknown[], load: () => Promise<T>) =>
    metadataCache.get(JSON.stringify([getSourceCacheScope(), id, id==='animeyt'?'individual-servers-v2':'v1', operation, ...args]), load);
  return {
    id: source.id, name: source.name, website: source.website, lang: source.lang,
    getPopular: (page = 1) => cache('popular', [page], () => source.getPopular(page)),
    getLatest: (page = 1) => cache('latest-added', [page], () => source.getLatest ? source.getLatest(page) : source.getPopular(page)),
    getSearchFilters: source.getSearchFilters ? () => cache('filters', [], () => source.getSearchFilters!()) : undefined,
    search: (query, page = 1, filters = {}) => cache('search', [query, page, filters], () => source.search(query, page, filters)),
    getAnime: anime => cache('detail', [anime], () => source.getAnime(anime)),
    getEpisodes: anime => cache('episodes', [anime], () => source.getEpisodes(anime)),
    // Signed media URLs must be resolved again when opening an episode.
    getServers: async episode => resolveAnimeServers(await source.getServers(episode)),
  };
}
